/**
 * <trustpilot-reviews>
 *
 * Enhances the custom Trustpilot reviews block on the product page. The summary uses the
 * official Trustpilot "Mini" TrustBox widget, which renders its own live data (TrustScore,
 * star rating, review count) — so the numbers always match the Trustpilot profile. This
 * element just makes sure the widget initialises (in case the Trustpilot bootstrap script
 * loaded before the widget was in the DOM) and wires the "show more reviews" expander.
 *
 * Lazy-initialised via window.initLazyScript (defined in main.js) when it nears the viewport.
 */
class TrustpilotReviews extends HTMLElement {
  constructor() {
    super();
    const lazy = window.initLazyScript || ((el, cb) => cb());
    lazy(this, this.init.bind(this), 500);
  }

  init() {
    this.initWidget();
    this.sortAndLimitReviews();
    this.initShowMore();
    this.initReviewBodies();
  }

  initWidget() {
    const widget = this.querySelector('.trustpilot-widget');
    if (widget && window.Trustpilot) {
      window.Trustpilot.loadFromElement(widget, true);
    }
  }

  initShowMore() {
    const button = this.querySelector('[data-tp-more]');
    if (!button) return;

    const cards = Array.from(this.querySelectorAll('.m-tp-reviews__card'));
    const initiallyVisible = parseInt(this.getAttribute('data-initial-visible') || '2', 10);
    const reviewIncrement = parseInt(this.getAttribute('data-review-increment') || '4', 10);
    const finalCta = this.querySelector('[data-tp-final-cta]');
    let visibleCount = Math.min(initiallyVisible, cards.length);

    if (cards.length <= initiallyVisible) {
      button.setAttribute('hidden', '');
      if (finalCta) finalCta.removeAttribute('hidden');
      return;
    }

    const updateButtonLabel = () => {
      const remaining = cards.length - visibleCount;
      const nextCount = Math.min(reviewIncrement, remaining);
      const template =
        nextCount === 1
          ? button.getAttribute('data-more-template-one')
          : button.getAttribute('data-more-template-other');

      button.textContent = (template || `Show ${nextCount} more reviews`).replace(/\d+/, nextCount);
    };

    const updateVisibility = () => {
      cards.forEach((card, index) => {
        if (index < visibleCount) {
          card.removeAttribute('hidden');
        } else {
          card.setAttribute('hidden', '');
        }
      });

      const reachedLimit = visibleCount >= cards.length;
      button.toggleAttribute('hidden', reachedLimit);
      button.setAttribute('aria-expanded', String(visibleCount > initiallyVisible));
      if (finalCta) finalCta.toggleAttribute('hidden', !reachedLimit);

      if (!reachedLimit) updateButtonLabel();
    };

    button.addEventListener('click', () => {
      visibleCount = Math.min(visibleCount + reviewIncrement, cards.length);
      updateVisibility();
    });

    updateVisibility();
  }

  initReviewBodies() {
    this.querySelectorAll('[data-tp-review-read-more]').forEach((button) => {
      const body = button.previousElementSibling;
      if (!body || !body.matches('[data-tp-review-body]')) return;

      const moreLabel = button.getAttribute('data-more-label') || 'Read more';
      const lessLabel = button.getAttribute('data-less-label') || 'Read less';
      let expanded = false;

      button.addEventListener('click', () => {
        expanded = !expanded;
        body.classList.toggle('is-expanded', expanded);
        button.textContent = expanded ? lessLabel : moreLabel;
        button.setAttribute('aria-expanded', String(expanded));
      });
    });
  }

  sortAndLimitReviews() {
    const list = this.querySelector('.m-tp-reviews__list');
    if (!list) return;

    const reviewsLimit = parseInt(this.getAttribute('data-reviews-limit') || '10', 10);
    const cards = Array.from(list.querySelectorAll('.m-tp-reviews__card'));

    cards
      .sort((a, b) => {
        const aTime = Date.parse(a.getAttribute('data-review-date')) || 0;
        const bTime = Date.parse(b.getAttribute('data-review-date')) || 0;

        return bTime - aTime;
      })
      .forEach((card, index) => {
        if (index < reviewsLimit) {
          list.appendChild(card);
        } else {
          card.remove();
        }
      });
  }
}

customElements.define('trustpilot-reviews', TrustpilotReviews);
